import { createTheme, Theme } from '@mui/material/styles'
import { Typography } from '@mui/material/styles/createTypography'
import { ZIndex } from '@mui/material/styles/zIndex'

import {
  FallbackLanguageCode,
  MuiLocales,
} from './constants/localization/languages'
import i18n from './locales/i18n'

export const palette = {
  // palette should always match https://www.figma.com/design/l4VaIBOXbJn3L2bwajW4oL/rhapsody?node-id=3-7&t=jTDj19LIeqw8cSBu-4
  brandPrimary: '#5A98B1',
  brandSecondary: '#01294A',
  brandLight: '#F5F9FB',
  brandPageBg: '#FCFBFA',

  grayGray1: '#3C3838',
  grayGray2: '#918D8D',
  grayGray3: '#D8D8D8',
  grayGray4: '#F6F5F5',
  grayWhite: '#FFFFFF',

  uiError: '#E02020',
  uiNeedsAttention: '#E06C00',
  uiWarning: '#F8B752',
  uiSuccess: '#7DAC1E',
  uiGradientStart: '#4A0E04',
  uiGradientEnd: '#BAA297',

  shadesError: '#FBEEEC',
  shadesNeedsAttention: '#FEF7ED',
  shadesNormal: '#F5F9FB',
  shadesSuccess: '#F8FBF4',

  shadesDarkAlert: '#992115',
  shadesDarkWarning: '#B35600',
  shadesDarkSuccess: '#679115',

  marketingPrimary: '#43A9D2',
  marketingSecondary: '#4A0E04',
  marketingLight1: '#ECE4D9',
  marketingLight2: '#F7F4EE',

  chewyPrimary: '#1C49C2',
} as const

export type PaletteColor = keyof typeof palette

const DIVIDER_COLOR = '#E5E5E5' // not in the new palette
const FILTER_BORDER = '#C6C0C0' // not in the new palette
const DISABLED_LABEL = '#7B7777' // not in the new palette
const CAPTION_TEXT_COLOR = 'rgba(74,14,4,0.3)' // ??

const rawTheme = {
  zIndex: {
    /*
      resets the default 1050 value that overlaps some components
      since this mui component is used as usual styled button
    */
    fab: undefined,
    base: 0,
    searchShadow: 5,
    rightRail: 9,
    progress: 100,
    headerTopBar: 1201,
    max: 9999,
  },
  typography: {
    htmlFontSize: 10,
    fontWeightBold: 500,
    fontFamily: 'Rubik',
    h1: {
      fontStyle: 'normal',
      color: palette.brandSecondary,
      lineHeight: '52px',
      fontSize: 44,
      fontWeight: '700',
    },
    h3: {
      fontStyle: 'normal',
      color: palette.brandSecondary,
      fontWeight: '500',
      lineHeight: '24px',
      fontSize: 20,
      fontStretch: '100%',
    },
    h5: {
      color: palette.brandSecondary,
      fontWeight: '500',
    },
    h6: {
      color: palette.marketingSecondary,
    },
    caption: {
      color: CAPTION_TEXT_COLOR,
    },
    body1: {
      fontSize: '1.4rem',
      color: palette.grayGray1,
    },
    body2: {
      lineHeight: 1.5,
      color: palette.grayGray1,
      fontSize: '1.6rem',
    },
  },
  colors: {
    ...palette,

    // colors below are deprecated. Use colors from palette.

    /** @deprecated Use `theme.colors.shadesNeedsAttention` instead */
    actionNeededBackground: palette.shadesNeedsAttention,
    /** @deprecated */
    patientEditIcon: '#FAF8F5',
    /** @deprecated */
    userPickerBorder: '#979797',
    /** @deprecated */
    menuSubheader: '#9F9B9B',
    /** @deprecated Use `theme.colors.marketingPrimary` instead */
    markerHighlighted: palette.marketingPrimary,
    /** @deprecated Use `theme.colors.brandPrimary` instead */
    title: palette.brandPrimary,
    /** @deprecated Use `theme.colors.brandPrimary` instead */
    tabSelected: palette.brandPrimary,
    /** @deprecated Use `theme.colors.grayGray2` instead */
    lowAccentText: palette.grayGray2,
    /** @deprecated Use `theme.colors.grayGray2` instead */
    tabLabel: palette.grayGray2,
    /** @deprecated Use `theme.colors.grayGray2` instead */
    searchButton: palette.grayGray2,
    /** @deprecated */
    tabDisabled: '#FDFDFC',
    /** @deprecated */
    tabLabelDisabled: '#A09E9D',
    /** @deprecated Use `theme.colors.brandPrimary` instead */
    timelineTitle: palette.brandPrimary,
    /** @deprecated Use `theme.colors.brandPrimary` instead */
    editIconColor: palette.brandPrimary,
    /** @deprecated Use `theme.colors.brandPrimary` instead */
    editFormPrimaryText: palette.brandPrimary,
    /** @deprecated Use `theme.colors.brandPrimary` instead */
    link: palette.brandPrimary,
    /** @deprecated Use `theme.colors.brandPrimary` instead */
    profileTopBackground: palette.brandPrimary,
    /** @deprecated Use `theme.colors.grayWhite` instead */
    profileText: palette.grayWhite,
    /** @deprecated */
    sideText: '#9F9B9B',
    /** @deprecated Use `theme.colors.marketingPrimary` instead */
    loginButtonBackground: palette.marketingPrimary,
    /** @deprecated */
    loginPageBackground: 'linear-gradient(140.31deg, #DDCFBD 0%, #F8F4EF 100%)',
    /** @deprecated Use `theme.colors.grayWhite` instead */
    splashScreenBackground:
      'linear-gradient(136.85deg, #EEE6DC 0%, #FAF7F2 100%)',
    /** @deprecated Use `theme.colors.grayWhite` instead */
    loginFormBackground: palette.grayWhite,
    /** @deprecated Use `theme.colors.grayGray4` instead */
    loginButtonText: palette.grayGray4,
    /** @deprecated Use `theme.colors.grayGray4` instead */
    tableEvenItem: palette.grayGray4,
    /** @deprecated Use `theme.colors.grayGray4` instead */
    soapStatusBar: palette.grayGray4,
    /** @deprecated Use `theme.colors.grayGray4` instead */
    dialogHeaderBackground: palette.grayGray4,
    /** @deprecated Use `theme.marketingSecondary  */
    loginPageSecondaryText: palette.marketingSecondary,
    /** @deprecated */
    searchPickerText: '#7F7373',
    /** @deprecated */
    defaultAvatarBackground: '#7F7373',
    /** @deprecated */
    patientAvatarBackground: '#E5E5E5',
    /** @deprecated */
    searchIconBackground: '#E5E5E5',
    /** @deprecated Use `theme.colors.grayWhite` instead */
    badgeColor: palette.grayWhite,
    /** @deprecated */
    searchPicker: '#EDECEC',
    /** @deprecated Use `theme.colors.marketingLight1` instead */
    iconBackground: palette.marketingLight1,
    /** @deprecated Use `theme.colors.brandSecondary` instead */
    iconText: palette.brandSecondary,
    /** @deprecated Use `theme.colors.marketingSecondary` instead */
    iconColor: palette.marketingSecondary,
    /** @deprecated */
    iconHover: '#936F60',
    /** @deprecated Use `theme.colors.brandSecondary` instead */
    primaryText: palette.brandSecondary,
    /** @deprecated */
    tertiaryText: '#2C0903',
    /** @deprecated Use `theme.colors.grayGray1` instead */
    selectedItem: palette.grayGray1,
    /** @deprecated */
    divider: DIVIDER_COLOR,
    /** @deprecated */
    listItemHover: '#EAE9E8',
    /** @deprecated Use `theme.colors.grayGray1` instead */
    listItemHoverText: palette.grayGray1,
    /** @deprecated Use `theme.colors.grayGray1` instead */
    userPickerText: palette.grayGray1,
    /** @deprecated Use `theme.colors.uiError` instead */
    errorColor: palette.uiError,
    /** @deprecated */
    alertError: '#D7574A',
    /** @deprecated Use `theme.colors.uiError` instead */
    alertLabelError: palette.uiError,
    /** @deprecated Use `theme.colors.grayWhite` instead */
    alertErrorText: palette.grayWhite,
    /** @deprecated Use `theme.colors.uiWarning` instead */
    alertWarning: palette.uiWarning,
    /** @deprecated */
    alertWarningText: '#2C0903',
    /** @deprecated */
    alertSuccess: '#96C735',
    /** @deprecated Use `theme.colors.brandPageBg` instead */
    contentBackground: palette.brandPageBg,
    /** @deprecated */
    filterBackground: '#F8F7F7',
    /** @deprecated Use `theme.colors.grayWhite` instead */
    tableBackground: palette.grayWhite,
    /** @deprecated Use `theme.brandLight` instead */
    tableLeftColumnBackground: palette.brandLight,
    /** @deprecated Use `theme.brandLight` instead */
    newLabel: palette.brandLight,
    /** @deprecated Use `theme.brandLight` instead */
    soapListItemHover: palette.brandLight,
    /** @deprecated Use `theme.colors.grayGray1 instead  */
    secondaryText: palette.grayGray1,
    /** @deprecated */
    expandMoreIcon: '#828080',
    /** @deprecated */
    subText: '#270601',
    /** @deprecated Use `theme.colors.uiSuccess` instead */
    success: palette.uiSuccess,
    /** @deprecated Use `theme.colors.uiNeedsAttention` instead */
    important: palette.uiNeedsAttention,
    /** @deprecated */
    successBackground: '#F8FBF3',
    /** @deprecated */
    filterBorderColor: FILTER_BORDER,
    /** @deprecated */
    panelRow: '#F7F7F7',
    /** @deprecated Use `theme.colors.grayGray3` instead */
    selectedOption: palette.grayGray3,
    /** @deprecated Use `theme.colors.grayGray3` instead */
    signIconBackground: palette.grayGray3,
    /** @deprecated Use `theme.colors.grayGray4` instead */
    tableOddRowBackground: palette.grayGray4,
    /** @deprecated */
    dosageCalculator: '#FCFBF8',
    /** @deprecated */
    disabledLabel: 'rgba(159,155,155,0.1)',
    /** @deprecated */
    disabledLabelText: DISABLED_LABEL,
    /** @deprecated */
    inactive: '#F7F7F6',
    /** @deprecated */
    eulaPageSection: '#F6F4F5',
    /** @deprecated */
    groupHeader: '#928D8D',
    /** @deprecated */
    styledButton: '#77161B',
    /** @deprecated */
    promptLabel: '#757070',
    /** @deprecated */
    toggleIcon: 'rgba(0, 0, 0, 0.54)',
    /** @deprecated Use `theme.colors.ui.shadesSuccess` instead */
    completedTextColor: palette.shadesSuccess,
    /** @deprecated */
    planBranding: '#1159E3',
    /** @deprecated */
    planWarning: '#EB7100',
    /** @deprecated colors.shadesDarkWarning */
    planSuspensed: palette.shadesDarkWarning,
    /** @deprecated */
    captionText: CAPTION_TEXT_COLOR,
    /** @deprecated */
    goodGrade: '#B1CD78',
    /** @deprecated Use `theme.colors.grayGray2 */
    inactiveBackground: palette.grayGray2,
  },
  transitions: {
    duration: {
      enteringScreen: 200,
      leavingScreen: 150,
    },
  },
  constants: {
    appHeader: {
      searchHeightMdDown: 53,
      heightMdDown: 48,
      heightMdUp: 72,
    },
    soapHeaderHeight: 99,
    soapProblemsSearchHeight: 58,
    handwritingBorder: '1px dashed #9F9B9B',
    handwritingBorderError: `1px dashed ${palette.uiError}`,
    controlBorder: '1px solid #EDECEC',
    tableBorder: '1px solid #EAE9E8',
    tableBorderHeavy: `2px solid ${palette.grayGray1}`,
    stripedTableHeaderBorder: `1px solid ${palette.grayGray1}`,
    tableBorderSelected: `1px solid ${palette.brandPrimary}`,
    tabBorder: `1px solid ${palette.grayGray3}`,
    filterBorder: `1px solid ${FILTER_BORDER}`,
    importantBorder: `1px solid ${palette.uiNeedsAttention}`,
    totalBorder: '1px solid #E6E8E9',
    pickerBorder: '1px solid #EBEBEB',
    hoverBorder: `1px solid ${palette.grayGray2}`,
    inactiveBorder: '1px solid #9F9B9B',
    outlinedInputBorder: '1px solid #D2CDCD',
    focusedInputBorder: '1px solid #4F0200',
    fabBorder: '1px solid #E5E5E5',
    errorBorder: `1px solid ${palette.uiError}`,
    locationPickerWidth: 248,
    rightRailCollapsedWidth: 360,
    rightRailExpandedWidth: 814,
    schedulerRowHeight: 103,
    schedulerNotLastChildPadding: 2,
    schedulerColumnWidth: 208,
    schedulerCardBorderWidth: 2,
    schedulerHeadingContainerHeight: 96,
    whiteboardHeadingContainerHeight: 116,
    boardingColumnWidth: 102,
    listItemShadow: '0 18px 10px -20px #B9B5B5',
    listItemHeaderShadow: '0 13px 8px -11px #B9B5B5',
    listItemHeaderSelectedShadow: '0 16px 8px -11px #B9B5B5',
    selectItemSelectedShadow: '0 4px 20px 0 rgba(0, 0,0 , 0.14)',
    activeFieldBorder: `1px solid ${palette.brandPrimary}`,
    blockShadow:
      '0 2px 4px 0 rgba(0,0,0,0.2), 3px 3px 20px 0 rgba(168,163,163,0.5)',
    buttonBlockShadow:
      '1px 1px 2px 0 rgba(153,153,153,0.14), 0 1px 4px 0 rgba(153,153,153,0.12)',
    dosageCalculatorShadow: 'inset 0 1px 7px 0 rgba(0,0,0,0.2)',
    leftMenuExpandedWidth: 290,
    leftMenuWidth: 57,
    soapStatusBarHeight: 40,
    soapStepperHeight: 52,
    soapInlineSearchHeight: 56,
    progressBarHeight: 3,
    clientAndPatientSectionWidth: 265,
    inactiveOpacity: 0.3,
    blurStyle: {
      filter: 'blur(8px)',
      '-webkit-filter': 'blur(8px)',
    },
    scrollbar: {
      '&::-webkit-scrollbar-track': {
        borderLeft: '1px solid #C6C0C0',
        borderRight: '1px solid #C6C0C0',
        backgroundColor: '#FBFBFB',
      },
      '&::-webkit-scrollbar': {
        width: 16,
        // borderLeft: '1px solid #C6C0C0',
        backgroundColor: '#FBFBFB',
      },
      '&::-webkit-scrollbar-thumb': {
        border: '3px solid transparent',
        backgroundClip: 'content-box',
        borderRadius: 10,
        backgroundColor: '#E5E5E5',
      },
    },
    resetPasswordStyle: {
      '&:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 30px ${palette.grayWhite} inset !important`,
      },
    },
    businessSwitchWidth: 370,
    businessSwitchWidthLg: 250,
    businessSwitchWidthMd: 180,
    tableHeaderHeight: 80,
  },
  utils: {
    modifyZIndex: (
      zIndex: number,
      position: 'above' | 'below',
      level: number = 1,
    ): number => zIndex + level * (position === 'above' ? 1 : -1),
    getPlaceholder: (props?: Typography) => {
      const placeholder = {
        color: 'current',
        opacity: 1,
        ...props,
      }
      return {
        '&::-webkit-input-placeholder': placeholder,
        '&::-moz-placeholder': placeholder, // Firefox 19+
        '&:-ms-input-placeholder': placeholder, // IE 11
        '&::-ms-input-placeholder': placeholder, // Edge
      }
    },
  },
  palette: {
    primary: {
      main: palette.brandPrimary,
    },
    text: {
      primary: palette.brandSecondary,
      secondary: palette.grayGray1,
    },
    secondary: {
      main: palette.grayGray1,
    },
    error: {
      main: palette.uiError,
    },
  },
  components: {
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
        colorInherit: {
          color: palette.grayWhite,
          fontWeight: 'bold',
          transition:
            'box-shadow 0.2s cubic-bezier(0.4, 0, 1, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: 'none',
          '&&&&:hover': {
            boxShadow: `0 14px 26px -12px rgba(105, 172, 199, 0.42),
            0 4px 23px 0px rgba(0, 0, 0, 0.12),
            0 8px 10px -5px rgba(105, 172, 199, 0.2)`,
            backgroundColor: '#69ACC7',
          },
          backgroundColor: palette.brandPrimary,
          '&&&&:active': {
            boxShadow: 'none',
            backgroundColor: '#437F98',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiFab: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
        primary: {
          color: '#9F9B9B',
          fontWeight: 'bold',
          borderRadius: 23.5,
          backgroundColor: 'white',
          transition:
            'box-shadow 0.2s cubic-bezier(0.4, 0, 1, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: 'none',
          border: '1px solid #E5E5E5',
          '&&&&:hover': {
            boxShadow: `0 14px 26px -12px rgba(0, 0, 0, 0.42),
            0 4px 23px 0px rgba(0, 0, 0, 0.12),
            0 8px 10px -5px rgba(0, 0, 0, 0.2)`,
            backgroundColor: '#F7F7F7',
          },
          '&&&&:active': {
            boxShadow: 'none',
            backgroundColor: '#F7F5F5',
          },
        },
        secondary: {
          color: palette.grayWhite,
          fontWeight: 'bold',
          backgroundColor: palette.marketingSecondary,
          transition:
            'box-shadow 0.2s cubic-bezier(0.4, 0, 1, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: 'none',
          '&&&&:hover': {
            boxShadow: `0 14px 26px -12px rgba(74, 14, 4, 0.42),
            0 4px 23px 0px rgba(0, 0, 0, 0.12),
            0 8px 10px -5px rgba(74, 14, 4, 0.2)`,
            backgroundColor: '#5B160B',
          },
          '&&&&:active': {
            boxShadow: 'none',
            backgroundColor: '#270601',
          },
        },
        colorInherit: {
          color: palette.grayWhite,
          fontWeight: 'bold',
          transition:
            'box-shadow 0.2s cubic-bezier(0.4, 0, 1, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: 'none',
          '&&&&:hover': {
            boxShadow: `0 14px 26px -12px rgba(105, 172, 199, 0.42),
            0 4px 23px 0px rgba(0, 0, 0, 0.12),
            0 8px 10px -5px rgba(105, 172, 199, 0.2)`,
            backgroundColor: '#69ACC7',
          },
          backgroundColor: palette.brandPrimary,
          '&&&&:active': {
            boxShadow: 'none',
            backgroundColor: '#437F98',
          },
        },
      },
    },
    MuiMenuItem: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          color: palette.grayGray1,
        },
      },
    },
    MuiStepButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          color: '#9F9B9B',
        },
        colorSecondary: {
          '&.Mui-checked': {
            color: palette.brandPrimary,
          },
        },
      },
    },
    MuiToggleButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'standard',
      },
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '1.4rem',
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: 'transparent',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: DIVIDER_COLOR,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          width: 'calc(100% - 32px)',
        },
        shrink: {
          color: DISABLED_LABEL,
          width: '100% !important',
          transform: 'translate(0, 4px) scale(0.71)',
        },
        formControl: {
          transform: 'translate(0, 22px) scale(1)',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        marginNormal: {
          marginTop: '8px',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          marginLeft: '3px',
          fontSize: '16px',
          lineHeight: '20px',
          '&.Mui-focused': {
            color: DISABLED_LABEL,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: 16,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D2CDCD',
            borderWidth: 1,
            borderRadius: 2,
          },
          '&:hover:not($disabled):not($focused):not($error) .MuiOutlinedInput-notchedOutline':
            {
              borderColor: '#D2CDCD',
              // Reset on touch devices, it doesn't add specificity
              '@media (hover: none)': {
                borderColor: '#D2CDCD',
              },
            },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.marketingSecondary,
            borderWidth: 1,
          },
        },
        multiline: {
          color: palette.grayGray1,
          marginTop: 4,
          padding: '8px 4px 8px',
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          color: palette.grayGray1,
          fontSize: 16,
          padding: '5px 2px 5px',
        },
        underline: {
          '&:before': {
            borderBottom: `1px solid ${palette.grayGray1}`,
          },
          '&:after': {
            borderWidth: '0 0 2px 0',
            borderStyle: 'solid',
            borderImageSlice: '2px',
            borderImage: `linear-gradient(145.53deg, ${palette.uiGradientStart} 0%, ${palette.uiGradientEnd} 100%) 47% 0%`,
          },
          '&&&&:hover:before': {
            borderWidth: '0 0 1px 0',
            borderStyle: 'solid',
            borderImageSlice: '1px',
            borderImage:
              'linear-gradient(145.53deg, #845A51 0%, #845A51 100%) 47% 0%',
          },
          '&.Mui-disabled': {
            '&&&&:hover:before': {
              borderWidth: '0 0 1px 0',
              borderStyle: 'solid',
              borderImageSlice: '1px',
              borderImage:
                'linear-gradient(145.53deg, #4A4A4A 0%, #BABABA 100%) 47% 0%',
            },
            '&:before': {
              borderWidth: '0 0 1px 0',
              borderStyle: 'solid',
              borderImageSlice: '1px',
              borderImage:
                'linear-gradient(145.53deg, #4A4A4A 0%, #BABABA 100%) 47% 0%',
            },
          },
          '&.Mui-error': {
            '&:after': {
              borderColor: palette.uiError,
              borderImage: 'none',
            },
          },
        },
        root: {
          fontSize: 14,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        colorSecondary: {
          '&.Mui-checked': {
            color: palette.brandPrimary,
          },
        },
      },
    },
    MuiStack: {
      defaultProps: {
        sx: {
          width: '100%',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '1.6rem',
        },
      },
    },
  },
  mixins: {
    toolbar: {
      minHeight: 72,
    },
  },
} as const

declare module '@mui/material/styles' {
  interface Theme {
    colors: typeof rawTheme.colors
    constants: typeof rawTheme.constants
    utils: typeof rawTheme.utils
    zIndex: typeof rawTheme.zIndex & Omit<ZIndex, 'fab'>
  }
  interface ThemeOptions {
    colors?: Partial<typeof rawTheme.colors>
    constants?: Partial<typeof rawTheme.constants>
    utils?: Partial<typeof rawTheme.utils>
  }
}

declare module '@mui/styles' {
  interface DefaultTheme extends Theme {}
}

declare module '@mui/styles/defaultTheme' {
  interface DefaultTheme extends Theme {}
}

const theme = createTheme(
  rawTheme,
  MuiLocales[i18n.language || FallbackLanguageCode],
)

theme.typography.h1[theme.breakpoints.down('md')] = {
  fontSize: 26,
  lineHeight: '31px',
}
theme.typography.h3[theme.breakpoints.down('md')] = {
  fontSize: 14,
  lineHeight: '17px',
}
theme.mixins.toolbar[theme.breakpoints.down('md')] = {
  minHeight: 45,
}

export default theme
export { rawTheme }
